import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
    Divider,
    Avatar,
    Chip,
    Alert,
    LinearProgress,
    useTheme,
} from '@mui/material';
import {
    Speed as SpeedIcon,
    Psychology as PsychologyIcon,
    Bolt as BoltIcon,
    Timer as TimerIcon,
    EmojiEvents as TrophyIcon,
    ArrowForward as ArrowForwardIcon,
    Refresh as RematchIcon,
    Share as ShareIcon,
    CheckCircle as CheckCircleIcon,
    VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useOfferDecision, OFFER_TYPES } from '../utils/offerDecision';
import api from '../services/api';
import FlexCardShareDialog from '../components/flex/FlexCardShareDialog';
import { trackFlexEvent, FLEX_EVENTS } from '../utils/flexAnalytics';

const READING_PHASES = {
    INTRO: 'intro',
    READING: 'reading',
    QUIZ: 'quiz',
    RESULTS: 'results',
    WAITING: 'waiting',
    ERROR: 'error',
};

export default function Challenge() {
    const { challengeId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const { user, isAuthenticated } = useAuth();
    const { offer, hasAccess } = useOfferDecision({ source: 'challenge' });

    const [phase, setPhase] = useState(READING_PHASES.INTRO);
    const [loading, setLoading] = useState(true);
    const [challenge, setChallenge] = useState(null);
    const [error, setError] = useState(null);
    const [readingStartTime, setReadingStartTime] = useState(null);
    const [readingElapsed, setReadingElapsed] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [generatedCard, setGeneratedCard] = useState(null);
    const [tabSwitches, setTabSwitches] = useState(0);
    const [focusLossCount, setFocusLossCount] = useState(0);
    const [attemptToken, setAttemptToken] = useState(null);
    const [guestId, setGuestId] = useState(null);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!isAuthenticated) {
            let gid = localStorage.getItem('guestId');
            if (!gid) {
                gid = 'guest-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
                localStorage.setItem('guestId', gid);
            }
            setGuestId(gid);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        async function fetchChallenge() {
            try {
                if (isAuthenticated) {
                    const res = await api.get(`/challenges/${challengeId}`);
                    setChallenge(res.data);

                    if (res.data.status === 'completed') {
                        setPhase(READING_PHASES.RESULTS);
                        setResult(res.data.results);
                    } else if (res.data.yourRole === 'challenger' && res.data.challengerCompleted) {
                        setPhase(READING_PHASES.WAITING);
                    } else {
                        setPhase(READING_PHASES.INTRO);
                    }
                } else {
                    // Guest: fetch preview so intro screen shows correct passage info
                    try {
                        const res = await api.get(`/challenges/${challengeId}/preview`);
                        if (res.data.status === 'completed') {
                            setPhase(READING_PHASES.RESULTS);
                        } else {
                            setChallenge({
                                passage: {
                                    title: res.data.passageTitle,
                                    wordCount: res.data.passageWordCount,
                                },
                                challenger: res.data.challenger,
                            });
                            setPhase(READING_PHASES.INTRO);
                        }
                    } catch (previewErr) {
                        setError(previewErr.response?.data?.error || 'Challenge not found.');
                        setPhase(READING_PHASES.ERROR);
                    }
                }
            } catch (err) {
                setError(err.response?.data?.error || 'Challenge not found.');
                setPhase(READING_PHASES.ERROR);
            } finally {
                setLoading(false);
            }
        }
        fetchChallenge();
    }, [challengeId, isAuthenticated]);

    useEffect(() => {
        if (phase === READING_PHASES.READING && readingStartTime) {
            timerRef.current = setInterval(() => {
                setReadingElapsed(Date.now() - readingStartTime);
            }, 100);
            return () => clearInterval(timerRef.current);
        }
    }, [phase, readingStartTime]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && phase === READING_PHASES.READING) {
                setTabSwitches(prev => prev + 1);
            }
        };
        const handleBlur = () => {
            if (phase === READING_PHASES.READING) {
                setFocusLossCount(prev => prev + 1);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
        };
    }, [phase]);

    const handleStartReading = async () => {
        if (!isAuthenticated && guestId) {
            try {
                const refCode = searchParams.get('ref') || null;
                const res = await api.post(`/challenges/${challengeId}/guest-accept`, { guestId, referralCode: refCode });
                setAttemptToken(res.data.attemptToken);
                setChallenge({
                    ...challenge,
                    passage: res.data.passage,
                    challenger: res.data.challenger,
                });
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to start challenge.');
                setPhase(READING_PHASES.ERROR);
                return;
            }
        } else if (isAuthenticated) {
            api.post(`/challenges/${challengeId}/start`).catch(() => {});
        }
        setReadingStartTime(Date.now());
        setPhase(READING_PHASES.READING);
        trackFlexEvent(FLEX_EVENTS.CHALLENGE_STARTED, { challenge_id: challengeId });
    };

    const handleFinishReading = () => {
        clearInterval(timerRef.current);
        setPhase(READING_PHASES.QUIZ);
        setQuizAnswers(new Array(challenge.passage.questions.length).fill(null));
    };

    const handleAnswerSelect = (qIndex, optionIndex) => {
        const newAnswers = [...quizAnswers];
        newAnswers[qIndex] = optionIndex;
        setQuizAnswers(newAnswers);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const readingTimeMs = Date.now() - readingStartTime;
            const wpm = Math.round((challenge.passage.wordCount / readingTimeMs) * 60000);

            const endpoint = isAuthenticated
                ? `/challenges/${challengeId}/submit`
                : `/challenges/${challengeId}/guest-submit`;

            const payload = isAuthenticated
                ? { wpm, answers: quizAnswers, readingTimeMs, tabSwitches, focusLossCount }
                : { attemptToken, guestId, wpm, answers: quizAnswers, readingTimeMs, tabSwitches, focusLossCount };

            const res = await api.post(endpoint, payload);

            setResult(res.data);
            if (res.data.status === 'completed') {
                setPhase(READING_PHASES.RESULTS);
                trackFlexEvent(FLEX_EVENTS.CHALLENGE_COMPLETED, {
                    challenge_id: challengeId,
                    outcome: res.data.results?.outcome,
                });
            } else {
                setPhase(READING_PHASES.WAITING);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to submit challenge.');
            setPhase(READING_PHASES.ERROR);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRematch = async () => {
        try {
            const res = await api.post(`/challenges/${challengeId}/rematch`);
            navigate(`/challenge/${res.data.challengeId}`);
        } catch (err) {
            setError('Failed to create rematch.');
        }
    };

    const handleShareResult = async () => {
        if (!result?.yourResult) return;
        try {
            const res = await api.post('/flex/cards/generate', {
                cardType: 'challenge',
                aspectRatio: '1:1',
                challengeResult: result.results?.outcome === 'tie' ? 'Tie' : (result.yourResult.user === result.results?.winner ? 'Victory' : 'Defeat'),
            });
            setGeneratedCard(res.data);
            setShareDialogOpen(true);
            trackFlexEvent(FLEX_EVENTS.CHALLENGE_FORWARDED, { challenge_id: challengeId });
        } catch (err) {
            console.error('Share result error:', err);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (phase === READING_PHASES.ERROR) {
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" color="error" gutterBottom>{error}</Typography>
                <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>Go Home</Button>
            </Container>
        );
    }

    // Results phase
    if (phase === READING_PHASES.RESULTS && result) {
        const challenger = result.challenger;
        const challenged = result.challenged;
        const yourResult = result.challenger?.user === user?.id ? result.challenger : result.challenged;
        const opponentResult = result.challenger?.user === user?.id ? result.challenged : result.challenger;
        const youWon = result.winner === user?.id;
        const isTie = result.outcome === 'tie';
        const isNoQualifier = result.outcome === 'no_qualifier';
        const edges = result.edges || {};
        const yourClass = yourResult?.comprehensionClass || 'qualifying';
        const oppClass = opponentResult?.comprehensionClass || 'qualifying';

        const classLabel = {
            qualifying: null,
            comprehension_limited: 'Comprehension-Limited',
            not_qualifying: 'Below Comprehension Threshold',
        };

        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Helmet>
                    <title>Challenge Results | ReaderFLEX</title>
                </Helmet>

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <TrophyIcon sx={{ fontSize: 64, color: isNoQualifier ? '#9E9E9E' : (isTie ? '#9E9E9E' : (youWon ? '#FFD700' : '#90CAF9')), mb: 1 }} />
                    <Typography variant="h4" fontWeight={800}>
                        {isNoQualifier ? 'No Qualifier' : isTie ? "It's a Tie!" : youWon ? 'You Won!' : 'You Lost'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {isNoQualifier ? 'Neither participant reached 80% comprehension. No overall winner this round.' :
                         isTie ? 'Same effective WPM and comprehension — perfectly matched.' :
                         result.results?.reason === 'opponent_session_unverified' ? 'Opponent session could not be verified.' :
                         result.results?.reason === 'both_sessions_unverified' ? 'Neither session could be verified.' :
                         result.results?.reason === 'opponent_below_comprehension_threshold' ? 'Opponent did not meet the comprehension threshold.' :
                         result.results?.reason === 'comprehension_tiebreaker' ? 'Tie broken by comprehension score.' :
                         youWon ? 'Your effective WPM beat your opponent.' :
                         'Your opponent had a higher effective WPM this round.'}
                    </Typography>
                    {(yourClass !== 'qualifying' || oppClass !== 'qualifying') && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {classLabel[yourClass] && (
                                <Chip label={`You: ${classLabel[yourClass]}`} size="small" color={yourClass === 'not_qualifying' ? 'error' : 'warning'} />
                            )}
                            {classLabel[oppClass] && (
                                <Chip label={`Opponent: ${classLabel[oppClass]}`} size="small" color={oppClass === 'not_qualifying' ? 'error' : 'warning'} />
                            )}
                        </Box>
                    )}
                </Box>

                {/* Head-to-head comparison */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom textAlign="center">Head-to-Head</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Avatar sx={{ mx: 'auto', mb: 1, bgcolor: theme.palette.primary.main }}>
                                You
                            </Avatar>
                            <Typography variant="caption">You</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Avatar sx={{ mx: 'auto', mb: 1, bgcolor: theme.palette.secondary.main }}>
                                VS
                            </Avatar>
                            <Typography variant="caption">Opponent</Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {[
                        { label: 'WPM', icon: SpeedIcon, you: yourResult?.wpm, opp: opponentResult?.wpm },
                        { label: 'Comprehension', icon: PsychologyIcon, you: yourResult?.comprehension, suffix: '%', opp: opponentResult?.comprehension },
                        { label: 'Effective WPM', icon: BoltIcon, you: yourResult?.effectiveWpm, opp: opponentResult?.effectiveWpm, highlight: true },
                        { label: 'Correct Answers', icon: CheckCircleIcon, you: `${yourResult?.correctAnswers}/${yourResult?.totalQuestions}`, opp: `${opponentResult?.correctAnswers}/${opponentResult?.totalQuestions}` },
                    ].map((metric, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant={metric.highlight ? 'h5' : 'h6'} fontWeight={700}
                                    color={metric.you > metric.opp ? 'success.main' : 'text.primary'}>
                                    {metric.you}{metric.suffix || ''}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {metric.label}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant={metric.highlight ? 'h5' : 'h6'} fontWeight={700}
                                    color={metric.opp > metric.you ? 'success.main' : 'text.primary'}>
                                    {metric.opp}{metric.suffix || ''}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Paper>

                {/* Session quality indicators (neutral messaging) */}
                {(yourResult?.integrityLabel || opponentResult?.integrityLabel) && (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                        {yourResult?.integrityLabel && yourResult.integrityLabel !== 'Standard Session' && (
                            <Chip
                                icon={<VerifiedUserIcon />}
                                label={`You: ${yourResult.integrityLabel}`}
                                size="small"
                                color={yourResult.integrityState === 'verified' ? 'success' : yourResult.integrityState === 'invalid' ? 'error' : 'default'}
                                variant="outlined"
                            />
                        )}
                        {opponentResult?.integrityLabel && opponentResult.integrityLabel !== 'Standard Session' && (
                            <Chip
                                icon={<VerifiedUserIcon />}
                                label={`Opponent: ${opponentResult.integrityLabel}`}
                                size="small"
                                color={opponentResult.integrityState === 'verified' ? 'success' : opponentResult.integrityState === 'invalid' ? 'error' : 'default'}
                                variant="outlined"
                            />
                        )}
                    </Box>
                )}

                {/* Adjusted score note */}
                {yourResult?.baseEffectiveWpm && yourResult?.effectiveWpm !== yourResult.baseEffectiveWpm && (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Effective WPM adjusted from {yourResult.baseEffectiveWpm} to {yourResult.effectiveWpm} based on session conditions
                        </Typography>
                    </Box>
                )}

                {/* Nuanced edge labels */}
                {(edges.speed || edges.comprehension) && (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                        {edges.speed && (
                            <Chip
                                icon={<SpeedIcon />}
                                label={`Speed edge: ${edges.speed === 'challenger' ? (yourResult === result.challenger ? 'You' : 'Opponent') : (yourResult === result.challenged ? 'You' : 'Opponent')}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {edges.comprehension && (
                            <Chip
                                icon={<PsychologyIcon />}
                                label={`Comprehension edge: ${edges.comprehension === 'challenger' ? (yourResult === result.challenger ? 'You' : 'Opponent') : (yourResult === result.challenged ? 'You' : 'Opponent')}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                            />
                        )}
                    </Box>
                )}

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {isAuthenticated && (
                        <Button variant="contained" startIcon={<RematchIcon />} onClick={handleRematch}>
                            Rematch
                        </Button>
                    )}
                    <Button variant="outlined" startIcon={<ShareIcon />} onClick={handleShareResult}>
                        Share Result
                    </Button>
                    <Button variant="text" endIcon={<ArrowForwardIcon />} onClick={() => navigate(offer.route)}>
                        {offer.label}
                    </Button>
                </Box>

                {/* Guest post-results CTA */}
                {!isAuthenticated && (
                    <Paper sx={{ p: 3, mt: 4, textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            Save Your Result and Keep Training
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Create a free account to save your challenge result, track progress, and challenge others.
                            Your score and referral attribution will transfer automatically.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => navigate(`/register?ref=${result?.referralCode || ''}&source=challenge&challengeId=${challengeId}&guestId=${guestId || ''}`)}
                            >
                                Create Free Account
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(offer.route)}
                            >
                                {offer.label}
                            </Button>
                        </Box>
                    </Paper>
                )}

                <FlexCardShareDialog
                    open={shareDialogOpen}
                    card={generatedCard}
                    onClose={() => setShareDialogOpen(false)}
                />
            </Container>
        );
    }

    // Waiting phase
    if (phase === READING_PHASES.WAITING) {
        return (
            <Container maxWidth="sm" sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress sx={{ mb: 3 }} />
                <Typography variant="h5" gutterBottom>Waiting for your opponent...</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    We'll show the results here once they complete the challenge.
                </Typography>
                <Button variant="outlined" onClick={() => navigate(offer.route)}>
                    {offer.label}
                </Button>
            </Container>
        );
    }

    // Intro phase (authenticated with challenge data, or guest without)
    if (phase === READING_PHASES.INTRO) {
        const challengerName = challenge?.challenger?.displayName || 'Someone';
        const passageTitle = challenge?.passage?.title || 'a reading challenge';
        const wordCount = challenge?.passage?.wordCount || 500;

        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Helmet>
                    <title>{`${challengerName} challenged you | ReaderFLEX`}</title>
                </Helmet>

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar
                        src={challenge?.challenger?.avatarUrl}
                        sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: theme.palette.primary.main }}
                    >
                        {challengerName?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Typography variant="h5" fontWeight={700}>
                        @{challengerName} challenged you!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        Read the passage and answer 5 questions. Highest effective WPM wins.
                    </Typography>
                </Box>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>{passageTitle}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip icon={<TimerIcon />} label={`${wordCount} words`} size="small" />
                        <Chip icon={<PsychologyIcon />} label="5 questions" size="small" />
                        <Chip icon={<BoltIcon />} label="Speed + comprehension" size="small" />
                    </Box>
                </Paper>

                {!isAuthenticated && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        No account needed to take the challenge. Create one after to save your results and keep training.
                    </Alert>
                )}

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleStartReading}
                        startIcon={<SpeedIcon />}
                        sx={{ py: 1.5, px: 5 }}
                    >
                        Accept Challenge
                    </Button>
                </Box>
            </Container>
        );
    }

    // Reading phase
    if (phase === READING_PHASES.READING && challenge) {
        const elapsedSeconds = Math.floor(readingElapsed / 1000);
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">{challenge.passage.title}</Typography>
                    <Chip icon={<TimerIcon />} label={`${elapsedSeconds}s`} color="primary" />
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (elapsedSeconds / 180) * 100)}
                    sx={{ mb: 3 }}
                />

                <Paper sx={{ p: 4, mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                    {challenge.passage.text}
                </Paper>

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleFinishReading}
                        startIcon={<PsychologyIcon />}
                    >
                        I've Finished Reading — Start Quiz
                    </Button>
                </Box>
            </Container>
        );
    }

    // Quiz phase
    if (phase === READING_PHASES.QUIZ && challenge) {
        const questions = challenge.passage.questions;
        const q = questions[currentQuestion];
        const allAnswered = quizAnswers.every(a => a !== null);

        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">Question {currentQuestion + 1} of {questions.length}</Typography>
                    <LinearProgress
                        variant="determinate"
                        value={((currentQuestion + 1) / questions.length) * 100}
                        sx={{ flex: 1, ml: 2 }}
                    />
                </Box>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>{q.question}</Typography>
                    <RadioGroup
                        value={quizAnswers[currentQuestion]}
                        onChange={(e) => handleAnswerSelect(currentQuestion, parseInt(e.target.value))}
                    >
                        {q.options.map((opt, i) => (
                            <FormControlLabel
                                key={i}
                                value={i}
                                control={<Radio />}
                                label={opt.text}
                                sx={{
                                    mb: 1,
                                    border: 1,
                                    borderColor: quizAnswers[currentQuestion] === i ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    p: 1,
                                }}
                            />
                        ))}
                    </RadioGroup>
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        disabled={currentQuestion === 0}
                        onClick={() => setCurrentQuestion(prev => prev - 1)}
                    >
                        Previous
                    </Button>
                    {currentQuestion < questions.length - 1 ? (
                        <Button
                            variant="contained"
                            disabled={quizAnswers[currentQuestion] === null}
                            onClick={() => setCurrentQuestion(prev => prev + 1)}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={!allAnswered || submitting}
                            onClick={handleSubmit}
                            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                        >
                            {submitting ? 'Submitting...' : 'Submit Challenge'}
                        </Button>
                    )}
                </Box>
            </Container>
        );
    }

    return null;
}
